import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Textarea } from '@structural-climbing/ui'
import { Sheet } from '../../design/Sheet'
import { ChipTabs } from '../../design/ChipTabs'
import type { IdeaKind } from './useIdeas'

export const MAX_IDEA_LENGTH = 500

interface Props {
  isOpen: boolean
  onClose: () => void
  /** Ideas are only offered while the campaign runs. */
  campaignOpen: boolean
  /** What the field starts with, from a starter chip. */
  initialText: string
  /** Resolves true when it was sent; the sheet closes itself then. */
  onSend: (kind: IdeaKind, body: string) => Promise<boolean>
}

/**
 * Where an idea gets written. Opens with the field focused, so tapping the
 * question at the bottom of the page is already the start of typing.
 */
export function IdeaComposerSheet({ isOpen, onClose, campaignOpen, initialText, onSend }: Props) {
  const { t } = useTranslation()
  const [kind, setKind] = useState<IdeaKind>(campaignOpen ? 'idea' : 'feedback')
  const [text, setText] = useState(initialText)
  const [sending, setSending] = useState(false)
  const fieldRef = useRef<HTMLTextAreaElement>(null)

  // A fresh sheet each time it opens, starting from the chip that opened it.
  useEffect(() => {
    if (!isOpen) return
    setText(initialText)
    setKind(campaignOpen ? 'idea' : 'feedback')
    // After the slide-up, so iOS does not scroll the page to chase the caret.
    const id = setTimeout(() => {
      const el = fieldRef.current
      if (!el) return
      el.focus({ preventScroll: true })
      el.setSelectionRange(el.value.length, el.value.length)
    }, 320)
    return () => clearTimeout(id)
  }, [isOpen, initialText, campaignOpen])

  const isIdea = kind === 'idea'
  const body = text.trim()

  const submit = async () => {
    if (!body || sending) return
    setSending(true)
    const ok = await onSend(kind, body)
    setSending(false)
    if (ok) onClose()
  }

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      aria-label={t('ideas.title')}
      data-testid="idea-composer"
    >
      <div className="flex flex-col gap-3 overflow-y-auto px-5 pb-5 pt-1">
        {campaignOpen && (
          <ChipTabs
            aria-label={t('ideas.modeLabel')}
            value={kind}
            onChange={setKind}
            options={[
              { key: 'idea', label: t('ideas.modeIdea') },
              { key: 'feedback', label: t('ideas.modeFeedback') },
            ]}
          />
        )}
        <div>
          <h2 className="text-balance text-xl font-bold leading-tight">
            {isIdea ? t('ideas.ideaTitle') : t('ideas.feedbackTitle')}
          </h2>
          <p className="mt-1 text-sm text-foreground/55">
            {isIdea ? t('ideas.ideaLead') : t('ideas.feedbackLead')}
          </p>
        </div>
        <Textarea
          ref={fieldRef}
          aria-label={isIdea ? t('ideas.ideaTitle') : t('ideas.feedbackTitle')}
          value={text}
          onValueChange={(v) => setText(v.slice(0, MAX_IDEA_LENGTH))}
          placeholder={isIdea ? t('ideas.ideaExample') : t('ideas.feedbackExample')}
          minRows={4}
          maxRows={9}
          maxLength={MAX_IDEA_LENGTH}
          variant="bordered"
          classNames={{
            inputWrapper:
              'border-[var(--gym-line)] data-[focus=true]:!border-[var(--gym-primary)] bg-content1',
            input: 'text-[15px]',
          }}
        />
        <div className="flex justify-between gap-3 text-xs text-foreground/45">
          <span>{t('ideas.privacy')}</span>
          <span className="shrink-0 tabular-nums">
            {text.length}/{MAX_IDEA_LENGTH}
          </span>
        </div>
        <Button
          size="lg"
          radius="lg"
          isDisabled={!body}
          isLoading={sending}
          onPress={() => void submit()}
          className={`font-bold ${
            isIdea ? 'bg-[var(--gym-primary)] text-white' : 'bg-foreground text-background'
          }`}
        >
          {isIdea ? t('ideas.sendIdea') : t('ideas.send')}
        </Button>
      </div>
    </Sheet>
  )
}
